const COMMON_KEYS = ['id', 'key', 'name'];

function formatIdentifier(str: string): string {
	return str
		.replace(/_/g, ' ')
		.replace(/([a-z])([A-Z])/g, '$1 $2')
		.replace(/\s+/g, ' ')
		.trim()
		.toLowerCase();
}

function isIdentifier(str: string): boolean {
	return (/[a-z]/.test(str) && /[A-Z]/.test(str)) || /_/.test(str);
}

function formatValue(str: string, shouldLowercase = false): string {
	const trimmed = str.trim();
	if (shouldLowercase) {
		return formatIdentifier(trimmed);
	}
	return isIdentifier(trimmed) ? formatIdentifier(trimmed) : trimmed;
}

function extractCommonKeyValues(obj: Record<string, unknown>): string[] {
	return COMMON_KEYS.filter(
		(key) => key in obj && (obj[key] === null || typeof obj[key] !== 'object'),
	).map((key) => {
		const value = String(obj[key]);
		if (key === 'name') {
			return value;
		}
		return formatValue(value);
	});
}

function formatObject(obj: Record<string, unknown>): string {
	const parts: string[] = [];

	for (const [key, value] of Object.entries(obj)) {
		const formattedKey = formatIdentifier(key);

		if (COMMON_KEYS.includes(key) && (value === null || typeof value !== 'object')) {
			parts.push(formatValue(String(value)));
			continue;
		}

		if (Array.isArray(value)) {
			if (value.length > 0 && typeof value[0] === 'object' && value[0] !== null) {
				const formatted = value
					.map((item) => {
						if (typeof item === 'object' && item !== null) {
							const commonValues = extractCommonKeyValues(item as Record<string, unknown>);
							return commonValues.length > 0
								? `(${commonValues.join(', ')})`
								: `(${formatObject(item as Record<string, unknown>)})`;
						}
						return String(item);
					})
					.join(', ');
				parts.push(`${formattedKey} ${formatted}`);
			} else {
				const formatted = value.map((item) => formatValue(String(item))).join(', ');
				parts.push(`${formattedKey}: ${formatted}`);
			}
			continue;
		}

		if (value !== null && typeof value === 'object') {
			const nested = value as Record<string, unknown>;
			const nestedKeys = Object.keys(nested);
			const commonKeys = nestedKeys.filter((k) => COMMON_KEYS.includes(k));

			if (commonKeys.length === 1 && nestedKeys.length === 1) {
				parts.push(`${formattedKey}: ${formatValue(String(nested[commonKeys[0]]))}`);
			} else {
				parts.push(`${formattedKey}: (${formatObject(nested)})`);
			}
			continue;
		}

		parts.push(`${formattedKey}: ${formatValue(String(value))}`);
	}

	return parts.join(', ');
}

export function sanitizeFieldName(value: unknown): string {
	if (typeof value === 'string') {
		return formatIdentifier(value);
	}
	if (Array.isArray(value)) {
		return value.map(String).join(',');
	}
	if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
		return formatObject(value as Record<string, unknown>);
	}
	return String(value);
}

export function sanitizeFieldValue(value: unknown): string {
	if (typeof value === 'string') {
		return formatValue(value);
	}
	if (Array.isArray(value)) {
		if (value.length > 0 && typeof value[0] === 'object' && value[0] !== null) {
			return value.map((item) => formatObject(item as Record<string, unknown>)).join('; ');
		}
		return value.map((item) => formatValue(String(item))).join(', ');
	}
	if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
		return formatObject(value as Record<string, unknown>);
	}
	return String(value);
}
